import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  fetchLeadFollowUpsFeed,
  followUpsSummaryText,
  isWonRow,
  rowName,
  undoFollowUpDone,
  UNDO_MS,
  type FollowUpsSummary,
  type LeadFollowUpRow,
} from './leadFollowUpsApi';
import { FollowUpDoneDialog } from './FollowUpDoneDialog';
import { undoDone, type DoneSaved } from './followUpDoneApi';
import { reopenTarget, type DoneReopenTarget } from './followUpPick';

/**
 * Personal mode of FollowUpsAdminPage, with no team assignment controls.
 * 2026-10-07 (Manak option 1): reads the same lead follow-ups feed as the
 * AURA Sales app, tags won leads "Won", and closes a follow-up with
 * "Done" (Undo for 5 s).
 * 2026-10-07 (Manak) — "✓ Mark done" opens the Done box (FollowUpDoneDialog):
 * recent calls first, else an outcome like the call Wrap Up. Undo reverts all
 * of it (doneEventId).
 */
export function PersonalFollowUps() {
  const [rows, setRows] = useState<LeadFollowUpRow[] | null>(null);
  const [summary, setSummary] = useState<FollowUpsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ row: LeadFollowUpRow; previous: string | null; doneEventId?: string | null } | null>(null);
  const [doneRow, setDoneRow] = useState<LeadFollowUpRow | null>(null);
  // 2026-10-09 — back from Tag sale / Tag proposal / New sale (#done=<lead>):
  // reopen the box for that lead; it picks up the draft + the pick.
  const location = useLocation();
  const navigate = useNavigate();
  const [reopen, setReopen] = useState<DoneReopenTarget | null>(() => reopenTarget(location.hash));
  useEffect(() => {
    if (location.hash.startsWith('#done=')) navigate(`${location.pathname}${location.search}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(() => {
    setError(null);
    fetchLeadFollowUpsFeed()
      .then((feed) => { setRows(feed.rows); setSummary(feed.summary); })
      .catch(() => setError('Could not load follow-ups. Retry to check again.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const showToast = (t: { row: LeadFollowUpRow; previous: string | null; doneEventId?: string | null } | null) => {
    if (timer.current) clearTimeout(timer.current);
    setToast(t);
    if (t) timer.current = setTimeout(() => setToast(null), UNDO_MS);
  };

  // Done takes the row out of the list: it no longer counts as scheduled
  // (nor overdue); Undo / a failure reloads or restores it.
  const shift = (row: LeadFollowUpRow, by: 1 | -1) => setSummary((s) => s && row.scheduledAt ? {
    ...s,
    scheduled: Math.max(0, s.scheduled + by),
    overdue: Math.max(0, s.overdue + (new Date(row.scheduledAt).getTime() < Date.now() ? by : 0)),
  } : s);

  // The box saved: the row leaves the list (a new date, if one was set,
  // comes back on the next load) and Undo stays up for 5 s.
  const saved = (row: LeadFollowUpRow, s: DoneSaved) => {
    setDoneRow(null);
    setRows((rs) => (rs ?? []).filter((r) => r.id !== row.id));
    shift(row, -1);
    showToast({ row, previous: s.previousFollowUpDate ?? row.scheduledAt, doneEventId: s.doneEventId });
  };

  const undo = async () => {
    if (!toast) return;
    const { row, previous, doneEventId } = toast;
    showToast(null);
    try {
      if (doneEventId) await undoDone(row.id, doneEventId);
      else if (previous) await undoFollowUpDone(row.id, previous);
      load();
    } catch {
      setError('Could not undo.');
    }
  };

  const now = Date.now();
  const pending = (rows ?? []).filter((r) => r.status === 'PENDING');
  const sorted = [...pending].sort((a, b) => {
    if (!a.scheduledAt) return 1;
    if (!b.scheduledAt) return -1;
    return a.scheduledAt.localeCompare(b.scheduledAt);
  });

  return <div className="space-y-4">
    <header className="flex justify-between">
      <div>
        <h1 className="text-2xl font-semibold">Follow-ups</h1>
        <p>My follow-ups.</p>
      </div>
      <button className="btn" data-tour="followups-refresh" onClick={load}>Refresh</button>
    </header>
    {error && <p className="text-sm text-red-600">{error} <button className="underline" onClick={load}>Retry</button></p>}
    {rows === null ? (!error && <p>Loading follow-ups…</p>) : <>
      {/* 2026-10-07 (tour bugs 1+2): dated rows are "scheduled"; rows with no
          date are counted apart. Same numbers as the My Day card. */}
      <p data-tour="followups-count">{summary ? followUpsSummaryText(summary) : ''}</p>
      {sorted.length === 0 ? <p data-tour="followups-list">No follow-ups due.</p> : <ul data-tour="followups-list" className="space-y-3">{sorted.map(row => {
        const late = !!row.scheduledAt && new Date(row.scheduledAt).getTime() < now;
        return <li key={row.id} className="card p-4 flex justify-between items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <strong>{rowName(row)}</strong>
              {isWonRow(row) && <span data-testid={`fu-won-tag-${row.id}`} className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">WON</span>}
            </div>
            <p className={late ? 'text-red-600' : undefined}>
              {row.scheduledAt
                ? new Date(row.scheduledAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                : 'No date set'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {row.scheduledAt && <button type="button" data-testid={`fu-done-${row.id}`} onClick={() => setDoneRow(row)}
              className="rounded-lg border border-emerald-200 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50">✓ Mark done</button>}
            <Link className="btn" to="/leads">Open my leads</Link>
          </div>
        </li>;
      })}</ul>}
    </>}
    {doneRow && <FollowUpDoneDialog
      target={{ name: rowName(doneRow), leadId: doneRow.id }}
      onClose={() => setDoneRow(null)}
      onSaved={(s) => saved(doneRow, s)}
    />}
    {reopen && !doneRow && <FollowUpDoneDialog
      target={{ name: reopen.name, leadId: reopen.leadId }}
      onClose={() => setReopen(null)}
      onSaved={(s) => {
        const row = (rows ?? []).find((r) => r.id === reopen.leadId);
        setReopen(null);
        if (row) saved(row, s); else load();
      }}
    />}
    {toast && <div role="status" className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-4 rounded-lg bg-gray-900 px-4 py-3 text-sm text-white shadow-lg">
      <span>Follow-up marked done</span>
      <button type="button" onClick={() => void undo()} className="font-semibold text-emerald-300 hover:text-emerald-200">Undo</button>
    </div>}
  </div>;
}
